import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { CommandLine } from "#/components/CommandLine.tsx";
import { capabilityName } from "#/components/PackageCard.tsx";
import { localeParam } from "#/lib/locale.ts";
import { npmPackage, runnerInstalls } from "#/lib/packages.ts";
import { getPackagePage } from "#/server/functions.ts";

/** One package at `/packages/<slug>`, with its install commands and README; 404 when unknown. */
export const Route = createFileRoute("/{-$locale}/packages/$name")({
  loader: async ({ params }) => {
    const page = await getPackagePage({ data: params.name });
    if (page === null) throw notFound();
    return page;
  },
  head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.pkg.name ?? ""} · pm` }] }),
  component: PackagePageView,
});

/**
 * The package's sentence, how to install it with pm, npx or bunx, its facts
 * (version, capabilities, curation, links) and its README.
 *
 * @returns The page.
 */
function PackagePageView() {
  const { locale, messages } = Route.useRouteContext();
  const { pkg, readme } = Route.useLoaderData();
  const t = messages.packagesPage;
  const runners = runnerInstalls(pkg.install);
  const npm = npmPackage(pkg.install);
  const installs = [
    { label: t.installPm, command: pkg.install },
    ...(runners === null ? [] : [{ label: t.installNpx, command: runners.npx }, { label: t.installBunx, command: runners.bunx }]),
  ];
  const links = [
    { label: t.repository, href: pkg.repo },
    { label: t.documentation, href: pkg.docs === "" ? undefined : pkg.docs },
    { label: "npm", href: npm === null ? undefined : `https://www.npmjs.com/package/${npm}` },
  ].filter((link): link is { label: string; href: string } => link.href !== undefined);
  let readmeBody: ReactNode;
  if (readme === null) readmeBody = <p>{t.noReadme}</p>;
  else if (readme.rendered === null) {
    readmeBody = (
      <p>
        {t.noReadme} <a href={readme.url}>{messages.docs.edit}</a>
      </p>
    );
  } else {
    readmeBody = (
      <>
        {/* Sanitised on the server by renderDoc: allow-listed tags, href/rel/id/class only. */}
        <div className="prose" dangerouslySetInnerHTML={{ __html: readme.rendered.html }} />
        <p className="docs-layout__source">
          {t.readmeSource} <a href={readme.url}>{messages.docs.edit}</a>
        </p>
      </>
    );
  }
  return (
    <div className="page package-page">
      <p className="eyebrow">
        <Link to="/{-$locale}/packages" params={{ locale: localeParam(locale) }}>
          {t.all}
        </Link>
      </p>
      <h1 translate="no">{pkg.name}</h1>
      <p className="lede">{pkg.description}</p>
      <div className="package-page__columns">
        <div className="package-page__main">
          <h2>{t.install}</h2>
          {pkg.install === "" ? (
            <p>{t.notInstallable}</p>
          ) : (
            <div className="stack">
              {installs.map((install) => (
                <div key={install.label} className="package-page__install">
                  <h3>{install.label}</h3>
                  <CommandLine command={install.command} label={install.label} messages={messages} />
                </div>
              ))}
            </div>
          )}
          <h2>{t.readme}</h2>
          {readmeBody}
        </div>
        <aside className="card package-facts" aria-labelledby="package-facts-title">
          <h2 id="package-facts-title" className="card__head">
            {t.about}
          </h2>
          <dl>
            {pkg.version === undefined ? null : (
              <div>
                <dt>{t.version}</dt>
                <dd translate="no">{pkg.version}</dd>
              </div>
            )}
            <div>
              <dt>{t.status}</dt>
              <dd>{[t.kinds[pkg.status], ...(pkg.preset ? [t.kinds.presets] : [])].join(" · ")}</dd>
            </div>
            {pkg.capabilities.length === 0 ? null : (
              <div>
                <dt>{t.capabilities}</dt>
                <dd>{pkg.capabilities.map((capability) => capabilityName(messages, capability)).join(", ")}</dd>
              </div>
            )}
            {links.length === 0 ? null : (
              <div>
                <dt>{t.links}</dt>
                <dd>
                  <ul>
                    {links.map((link) => (
                      <li key={link.label}>
                        <a href={link.href}>{link.label}</a>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
          </dl>
        </aside>
      </div>
    </div>
  );
}
